import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { getReivewCountsByPeriod } from '@/api/appstore.api';
import { type AppStore } from '@/interfaces/service/appstore/appstore.interface';
import FlexBox from '@/components/FlexBox';
import DashboardWatingDataFallback from '@/views/appstore/dashboard/components/DashboardWatingDataFallback';
import { DESKTOP_CONTENTS_MAX_WIDTH, MOBILE_MAX_WIDTH } from '@/app/stylesguides';
import theme from '@/app/theme';

const getPeriodOffsetLabel = (periodOffset: number) => {
  switch (periodOffset) {
    case 0:
      return '이번주';
    case 1:
      return '지난주';
    default:
      return `${periodOffset}주전`;
  }
};

// 데이터가 모두 0인지 체크하는 함수
const hasNoChartData = (data: Record<'periodOffset' | 'positiveCounts' | 'negativeCounts', number>[]) => {
  if (!data || data.length === 0) return true;
  return data.every((item) => item.positiveCounts === 0 && item.negativeCounts === 0);
};

const AppStorePostsBarChart = ({ currentAppStore, isMobile }: { currentAppStore: AppStore; isMobile: boolean }) => {
  const chartRef = useRef();

  const [reviewCounts, setReviewCounts] = useState<Record<'periodOffset' | 'positiveCounts' | 'negativeCounts', number>[]>();

  const [width, setWidth] = useState(0);

  const setChartWidth = () => {
    if (typeof window !== 'undefined') {
      const _isMobileSize = window.innerWidth <= MOBILE_MAX_WIDTH;

      const containerPadding = 20;
      const contentGap = 16;
      const screenPadding = _isMobileSize ? 16 : 20;

      const screen = window.innerWidth;

      if (_isMobileSize) {
        setWidth(screen - (screenPadding + containerPadding) * 2); //16, 20은 가로 여백
      } else {
        if (screen >= DESKTOP_CONTENTS_MAX_WIDTH) {
          const contentWidth = (DESKTOP_CONTENTS_MAX_WIDTH - contentGap) / 2;
          setWidth(contentWidth - containerPadding * 2);
        } else {
          const contentWidth = (screen - screenPadding * 2) / 2;
          setWidth(contentWidth - containerPadding * 2);
        }
      }
    }
  };

  useEffect(() => {
    const getReivewPostsCountsByPeriod = async () => {
      const res = await getReivewCountsByPeriod({
        appStoreId: currentAppStore.storeId,
        channelIdList: currentAppStore.channels.map((channel) => channel.channelId)
      });

      if (res?.isSuccess && res?.data) {
        const largestPeriodOffset = 4;

        const chartData = res.data.filter(({ periodOffset }) => periodOffset <= largestPeriodOffset);

        setReviewCounts(chartData.reverse());
      }
    };
    getReivewPostsCountsByPeriod();
    setChartWidth();

    if (window && reviewCounts) {
      window.addEventListener('resize', setChartWidth);
    }

    return () => window.removeEventListener('resize', setChartWidth);
  }, []);

  if (currentAppStore.status !== 'STABLE' || !reviewCounts) {
    return <DashboardWatingDataFallback message="리뷰를 분석하는 중입니다." />;
  } else if (reviewCounts) {
    const isEmptyData = hasNoChartData(reviewCounts);

    return (
      <Box position="relative" width="100%">
        <BarChart
          ref={chartRef}
          dataset={reviewCounts}
          series={[
            {
              dataKey: 'negativeCounts',
              label: '부정 리뷰',
              stack: 'total',
              stackOrder: 'reverse'
            },
            {
              dataKey: 'positiveCounts',
              label: '긍정 리뷰',
              stack: 'total',
              stackOrder: 'reverse'
            }
          ]}
          xAxis={[
            {
              scaleType: 'band',
              dataKey: 'periodOffset',
              valueFormatter: (periodOffset) => getPeriodOffsetLabel(periodOffset)
            }
          ]}
          colors={[theme.palette.error.dark, theme.palette.primary.main]}
          slotProps={{
            legend: {
              position: { vertical: 'top', horizontal: 'right' },
              labelStyle: {
                fontSize: '16px',
                fill: theme.palette.gray[800],
                order: 'reverse'
              },
              itemMarkHeight: 8,
              itemMarkWidth: 8,
              markGap: 8,
              itemGap: 16
            },
            axisLine: {
              style: {
                strokeWidth: 0
              }
            },
            axisTick: {
              style: {
                strokeWidth: 0
              }
            }
          }}
          borderRadius={4}
          width={width}
          height={isMobile ? 280 : 340}
        />

        {/* 데이터가 모두 0인 경우에만 오버레이 표시 */}
        {isEmptyData && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '8px'
            }}
          >
            <FlexBox
              style={{
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Typography variant="h4" color={theme.palette.gray[800]} textAlign="center">
                {isMobile ? (
                  <>
                    최근 5주간
                    <br />
                    수집된 리뷰가 없습니다
                  </>
                ) : (
                  '최근 5주간 수집된 리뷰가 없습니다'
                )}
              </Typography>
              <Typography variant="body1" color={theme.palette.gray[600]} textAlign="center">
                {isMobile ? (
                  <>
                    새로운 리뷰가 수집되면
                    <br />이 차트에 자동으로 반영됩니다
                  </>
                ) : (
                  '새로운 리뷰가 수집되면 이 차트에 자동으로 반영됩니다'
                )}
              </Typography>
            </FlexBox>
          </Box>
        )}
      </Box>
    );
  } else {
    return '';
  }
};

export default AppStorePostsBarChart;
